import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { formatAge } from '@/utils/ageHelper';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteAppendix5Mutation,
	useGetAppendix5DetailQuery,
} from '@/api/forms-management/appendix5';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
	useGetAppendix5GenerateReportMutation,
	useGetExportCustomerPDFMutation,
} from '@/api/common';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function Appendix5DetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, appendix5Id, admissionRequestId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteAppendix5] = useDeleteAppendix5Mutation();
	const showModal = useModal();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getAppendix5GenerateReport] = useGetAppendix5GenerateReportMutation();

	const [fromInformationExpanded, setFromInformationExpanded] = useState(true);
	const [toInformationExpanded, setToInformationExpanded] = useState(true);
	const [referralInformationExpanded, setReferralInformationExpanded] =
		useState(true);

	const { data: details }: any = useGetAppendix5DetailQuery(appendix5Id || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.appendix5ReceiptOfReferral'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteAppendix5(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	const formartPleaseBeInformedThat = () => {
		const arr = [];
		if (details?.data.caseHasBeenTakenUp)
			arr.push(
				t('preAdmission.caseHasBeenTakenUpBy') +
					' ' +
					details?.data.nameOfSocialWorker +
					' ' +
					details?.data.phoneOfSocialWorker
			);

		if (details?.data.lookIntoCase)
			arr.push(
				t('preAdmission.ourOfficeWillLookIntoCase') +
					' ' +
					(details?.data.lookIntoCaseDate
						? dayjs(details?.data.lookIntoCaseDate)
								.locale('en')
								.format(DISPLAY_DATE_FORMAT)
						: '')
			);
		if (details?.data.notBeFollowedUp)
			arr.push(
				t('preAdmission.caseWillNotFollowedByICCMWBecause') +
					' ' +
					details?.data.notBeFollowedUpBecause
			);

		if (details?.data.others)
			arr.push(
				t('preAdmission.othersSpecify') + ' ' + details?.data.othersSpecify
			);

		return arr.join(';\n');
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.appendix5Detail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getAppendix5GenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setFromInformationExpanded(!fromInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.from')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: fromInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={fromInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.fromOfficerInCharge}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.unit')}
									value={details?.data.branchName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.ref')}
									value={details?.data.fromRefNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.fromTelNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.faxNo')}
									value={details?.data.fromFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.date')}
									value={
										details?.data.fromDate
											? dayjs(details?.data.fromDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setToInformationExpanded(!toInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.to')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: toInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={toInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.toOfficerInCharge}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfTheOrganisation')}
									value={details?.data.nameOfOrganization}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.ref')}
									value={details?.data.toRefNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dated')}
									value={
										details?.data.toDated
											? dayjs(details?.data.toDated)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.faxNo')}
									value={details?.data.toFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.totalPages')}
									value={details?.data.toTotalPages}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setReferralInformationExpanded(!referralInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.referralForICCMW')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: referralInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={referralInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.hkicNo')}
									value={details?.data.hkicNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.sex')}
									value={details?.data.sexDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.age')}
									value={formatAge(
										details?.data.age,
										details?.data.ageEstimated
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.inRespectOfAboveReferralOn')}
									value={
										details?.data.referralDate
											? dayjs(details?.data.referralDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.pleaseBeInformedThat')}
									value={formartPleaseBeInformedThat()}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />
						<Box display='flex' justifyContent='space-between' marginBottom={2}>
							<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
								{t('preAdmission.shouldEnquiries')}
							</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name')}
									value={details?.data.contactWorkerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.contactTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.contactOfficerInCharge}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
